import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Flag, CheckCircle2 } from 'lucide-react';
import { MilestoneCard } from './gantt/MilestoneCard';
import { GanttHeader } from './gantt/GanttHeader';
import { GanttTimeline } from './gantt/GanttTimeline';
import { useGanttDrag } from './gantt/useGanttDrag';
import { PHASE_COLORS } from '@/components/visual/chartPalette';

export function GanttView({ results }) {
  const { t } = useTranslation();

  // Custom phase durations — reset when engine output changes
  const [phaseDurations, setPhaseDurations] = useState({});
  // Milestone label overrides (keyed by milestone index — list is fixed-size)
  const [milestoneLabels, setMilestoneLabels] = useState({});
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Reset custom durations when engine results change
  useEffect(() => {
    setPhaseDurations({});
  }, [results.totalDurationWeeks, results.totalEffort]);

  const basePhases = useMemo(() => results.phases || [], [results.phases]);

  // Merge engine phases with any custom durations
  const displayPhases = useMemo(() => {
    let cum = 0;
    return basePhases.map((phase, idx) => {
      const weeks = phaseDurations[phase.id] ?? phase.durationWeeks;
      const startWeek = cum;
      cum += weeks;
      return { ...phase, durationWeeks: weeks, startWeek, color: PHASE_COLORS[idx % PHASE_COLORS.length] };
    });
  }, [basePhases, phaseDurations]);

  const totalWeeks = useMemo(
    () => Math.max(1, displayPhases.reduce((sum, p) => sum + p.durationWeeks, 0)),
    [displayPhases]
  );
  const months = Math.ceil(totalWeeks / 4);
  const hasCustom = Object.keys(phaseDurations).length > 0;
  const resetDurations = () => setPhaseDurations({});

  // Drag logic encapsulated in a hook so the main component stays focused on layout
  const { isDragging, trackRefs, handleDragStart } = useGanttDrag({
    basePhases,
    displayPhases,
    phaseDurations,
    setPhaseDurations,
  });

  // --- Milestones ---
  const internalAuditWeek = Math.max(
    1, totalWeeks - (displayPhases[displayPhases.length - 1]?.durationWeeks || 0)
  );

  const milestones = useMemo(() => {
    const defaults = [
      { key: 'projectStart', fallback: 'Project Start', icon: Calendar, color: '#66707A', weekLabel: 'Week 1' },
      { key: 'preCertAudit', fallback: 'Internal Audit', icon: Flag, color: '#2867B2', weekLabel: `Week ${internalAuditWeek}` },
      { key: 'certTarget', fallback: 'Certification Target', icon: CheckCircle2, color: '#78B928', weekLabel: `Week ${totalWeeks}` },
    ];
    return defaults.map((m, i) => ({
      ...m,
      label: milestoneLabels[i] !== undefined ? milestoneLabels[i] : t(`gantt.${m.key}`, m.fallback),
    }));
  }, [internalAuditWeek, totalWeeks, milestoneLabels, t]);

  const saveMilestoneEdit = () => {
    setMilestoneLabels(prev => ({ ...prev, [editingMilestone]: editValue }));
    setEditingMilestone(null);
  };

  return (
    <div data-testid="gantt-view-section">
      <GanttHeader months={months} totalWeeks={totalWeeks} hasCustom={hasCustom} onReset={resetDurations} />

      <GanttTimeline
        months={months}
        totalWeeks={totalWeeks}
        hasCustom={hasCustom}
        displayPhases={displayPhases}
        phaseDurations={phaseDurations}
        isDragging={isDragging}
        trackRefs={trackRefs}
        onDragStart={handleDragStart}
        onKeyboardDurationChange={(id, weeks) => setPhaseDurations(prev => ({ ...prev, [id]: weeks }))}
        onReset={resetDurations}
      />

      {/* Milestone cards — labels are editable */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {milestones.map((m, i) => (
          <MilestoneCard
            key={m.key}
            index={i}
            milestone={m}
            isEditing={editingMilestone === i}
            editValue={editValue}
            setEditValue={setEditValue}
            onStartEdit={() => { setEditingMilestone(i); setEditValue(m.label); }}
            onSave={saveMilestoneEdit}
            onCancel={() => setEditingMilestone(null)}
          />
        ))}
      </div>
    </div>
  );
}
